import { useTranslation } from 'react-i18next';

import { ordinalX, useChartDirection } from './chart-utils';

export interface BarChartDatum {
  readonly id: string;
  readonly label: string;
  readonly value: number;
}

interface BarChartProps {
  readonly data: readonly BarChartDatum[];
  /** Summarizes the whole chart for assistive tech (`role="img"`). */
  readonly ariaLabel: string;
  readonly height?: number;
  readonly valueFormatter?: (value: number) => string;
}

const WIDTH = 480;
const PADDING = 24;
const LABEL_SPACE = 20;

/**
 * A small categorical bar chart (e.g. demand by prayer slot). RTL-aware
 * (category order mirrors so the chart reads start → end with the page), and
 * paired with a visually-hidden data table for screen readers.
 */
export function BarChart({
  data,
  ariaLabel,
  height = 200,
  valueFormatter,
}: BarChartProps): JSX.Element {
  const { t } = useTranslation();
  const dir = useChartDirection();

  if (data.length === 0) {
    return <p className="chart__empty">{t('common.empty')}</p>;
  }

  const format = valueFormatter ?? ((v: number) => String(v));
  const max = Math.max(1, ...data.map((d) => d.value));
  const innerWidth = WIDTH - PADDING * 2;
  const innerHeight = height - PADDING * 2 - LABEL_SPACE;
  const slotWidth = innerWidth / data.length;
  const barWidth = Math.min(40, slotWidth * 0.6);

  return (
    <figure className="chart chart--bar">
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="chart__svg"
      >
        <line
          x1={PADDING}
          y1={PADDING + innerHeight}
          x2={WIDTH - PADDING}
          y2={PADDING + innerHeight}
          className="chart__axis"
        />
        {data.map((d, i) => {
          const centerX = PADDING + ordinalX(i, data.length, innerWidth, dir);
          const barHeight = (d.value / max) * innerHeight;
          return (
            <g key={d.id}>
              <rect
                x={centerX - barWidth / 2}
                y={PADDING + innerHeight - barHeight}
                width={barWidth}
                height={barHeight}
                rx={4}
                className="chart__bar"
              />
              <text
                x={centerX}
                y={PADDING + innerHeight + LABEL_SPACE - 4}
                textAnchor="middle"
                className="chart__categoryLabel"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="sr-only">
        <table>
          <caption>{ariaLabel}</caption>
          <thead>
            <tr>
              <th scope="col">{t('admin.insights.tableCategory')}</th>
              <th scope="col">{t('admin.insights.tableValue')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.id}>
                <th scope="row">{d.label}</th>
                <td>{format(d.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </figcaption>
    </figure>
  );
}
